import { useEffect, useRef, useState } from "react";
import { useAppContext } from "../context/AppContext";
import { ItemModel } from "../models/ItemModel";
import itemService from "../services/itemService";

interface EditItemReturnPolicyCompProps {
    show: boolean;
    item: ItemModel;
    onClose: () => void;
    onItemReturnPolicyUpdated: (updatedItem: ItemModel) => void;
}

interface ItemReturnPolicyFormErrors {
    itemReturnPolicy?: string;
}

const EditItemReturnPolicyComp: React.FC<EditItemReturnPolicyCompProps> = ({
    show,
    item,
    onClose,
    onItemReturnPolicyUpdated
}) => {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const bsModalRef = useRef<any>(null);
    const { seller, setSeller } = useAppContext();

    const [storeReturnPolicy, setStoreReturnPolicy] = useState(seller!.returnPolicy);
    const [itemReturnPolicy, setItemReturnPolicy] = useState(item.itemReturnPolicy ?? '');
    const [useStoreReturnPolicy, setUseStoreReturnPolicy] = useState(item.useStoreReturnPolicy);
    const [useItemReturnPolicy, setUseItemReturnPolicy] = useState(item.useItemReturnPolicy);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [itemReturnPolicyFormErrors, setItemReturnPolicyFormErrors] = useState<ItemReturnPolicyFormErrors>({});
    const [serviceStatus, setServiceStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        if (modalRef.current) {
            bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current);
            modalRef.current.addEventListener("hidden.bs.modal", onClose);
        }
        return () => {
            modalRef.current?.removeEventListener("hidden.bs.modal", onClose);
        };
    }, []);

    useEffect(() => {
        if (show) {
            setStoreReturnPolicy(seller!.returnPolicy);
            setItemReturnPolicy(item.itemReturnPolicy ?? '');
            setUseStoreReturnPolicy(item.useStoreReturnPolicy);
            setUseItemReturnPolicy(item.useItemReturnPolicy);
            setServiceStatus({ type: '', message: '' });
            bsModalRef.current?.show();
        } else {
            bsModalRef.current?.hide();
        }
    }, [show]);

    const validateItemReturnPolicy = () => {
        const errors: ItemReturnPolicyFormErrors = {};
        if (useItemReturnPolicy && !itemReturnPolicy.trim()) {
            errors.itemReturnPolicy = "Item return policy is required if 'Use Item Return Policy' is selected.";
        }
        setItemReturnPolicyFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleItemReturnPolicySubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setServiceStatus({ type: "", message: "" });
        if (validateItemReturnPolicy()) {
            setIsSubmitting(true);
            try {
                const updatedItem = {
                    ...item,
                    useStoreReturnPolicy,
                    useItemReturnPolicy,
                    itemReturnPolicy
                };
                const result = await itemService.updateItemReturnPolicy(item.itemId, useStoreReturnPolicy, useItemReturnPolicy, itemReturnPolicy);
                if (result === "Success") {
                    onItemReturnPolicyUpdated(updatedItem);
                    setServiceStatus({ type: "success", message: "Item return policy updated successfully." });
                } else {
                    setServiceStatus({ type: "error", message: "Failed to update item return policy. Please try again." });
                }
            } catch (error) {
                setServiceStatus({ type: "error", message: "Failed to update item return policy." });
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleUseStoreReturnPolicyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setUseStoreReturnPolicy(checked);
    };

    const handleUseItemReturnPolicyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setUseItemReturnPolicy(checked);
        if (!checked) {
            
            setItemReturnPolicyFormErrors(prev => ({ ...prev, itemReturnPolicy: undefined }));
        }
    }

    return (
        <div className="modal fade" tabIndex={-1} ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
            <div className="modal-dialog modal-lg">
                <div className="modal-content rounded-0">
                    <form onSubmit={handleItemReturnPolicySubmit}>
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Return Policies</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => bsModalRef.current?.hide()}
                                disabled={isSubmitting}
                            ></button>
                        </div>
                        <div className="modal-body">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label">Use Store Return Policy</label>
                                    <div className="form-check form-switch fs-4 ms-2 mb-4">
                                        <input
                                            className="form-check-input fs-3"
                                            type="checkbox"
                                            id="useStoreReturnPolicySwitch"
                                            checked={useStoreReturnPolicy}
                                            onChange={handleUseStoreReturnPolicyChange}
                                        />
                                    </div>
                                    <textarea
                                        className="form-control bg-gray-100"
                                        rows={20}
                                        value={storeReturnPolicy}
                                        readOnly
                                        disabled
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Use Item Return Policy</label>
                                    <div className="form-check form-switch fs-4 ms-2 mb-4">
                                        <input
                                            className="form-check-input fs-3"
                                            type="checkbox"
                                            id="useItemReturnPolicySwitch"
                                            checked={useItemReturnPolicy}
                                            onChange={handleUseItemReturnPolicyChange}
                                        />
                                    </div>
                                    <textarea
                                        className={`form-control ${itemReturnPolicyFormErrors.itemReturnPolicy ? "is-invalid" : ""}`}
                                        rows={20}
                                        value={itemReturnPolicy}
                                        onChange={(e) => setItemReturnPolicy(e.target.value)}
                                        disabled={!useItemReturnPolicy}
                                        placeholder="Enter item-specific return policy"
                                    />
                                    {itemReturnPolicyFormErrors.itemReturnPolicy && (
                                        <div className="invalid-feedback">
                                            {itemReturnPolicyFormErrors.itemReturnPolicy}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {serviceStatus.type.toLowerCase() === "error" && (
                            <div className="col-md-12">
                                <div className="alert alert-danger p-2 mb-0">{serviceStatus.message}</div>
                            </div>
                        )}
                        {serviceStatus.type.toLowerCase() === "success" && (
                            <div className="col-md-12">
                                <div className="alert alert-success p-2 mb-0">{serviceStatus.message}</div>
                            </div>
                        )}

                        <div className="modal-footer d-flex justify-content-between">
                            <button
                                type="button"
                                className="btn btn-outline-dark rounded-0"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-dark rounded-0"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Saving ...
                                    </>
                                ) : (
                                    "Save"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div >
        </div >
    );
};
export default EditItemReturnPolicyComp;